import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { Controller } from "react-hook-form";
import { Control } from "react-hook-form/dist/types/form";
import styled, { useTheme } from "styled-components";
import {
  faCheckCircle,
  faExclamationCircle,
} from "@fortawesome/pro-solid-svg-icons";
import TooltipNew from "../common/TooltipNew";
import { LabelContainer } from "./dynamic/SelectNewTextField";
import { IconProp } from "@fortawesome/fontawesome-svg-core";

export interface TextFieldControllerProps {
  control: Control<any>;
  rules?: {
    required?: boolean;
    minLength?: number;
    maxLength?: number;
    pattern?: RegExp;
    valueAsNumber?: boolean;
    validate?: any;
    max?: number;
    min?: number;
  };
  noPositiveFeedback?: boolean;
  placeholder?: string;
  defaultValue: string | number | null | undefined;
  label?: string;
  fieldName: string;
  errorMessage?: string;
  errorMessageUntouched?: string;
  small?: boolean;
  tooltip?: string;
  disabled?: boolean;
  responsive?: boolean;
  className?: string;
  type?: React.HTMLInputTypeAttribute;
}

export const TextFieldWrapper = styled.div<{ responsive?: boolean }>`
  width: ${({ responsive }) => (responsive ? "100%" : "305px")};
`;

export const StyledLabel = styled.div<{ responsive?: boolean }>`
  width: ${({ responsive }) => (responsive ? "100%" : "305px")};
  padding-bottom: 10px;
  font-family: ${(props) => props.theme.fonts.normal};
  font-weight: 400;
  font-size: 15px;
`;

export const WarnLabel = styled.label`
  font-size: 11px;
  color: ${(props) => props.theme.colors.accent1};
  font-family: ${(props) => props.theme.fonts.normal};
  margin-top: 10px;
  display: inline-block;
`;

export const StyledInput = styled.input<{
  error: boolean;
  responsive?: boolean;
  maxWidth?: boolean;
}>`
  ${({ responsive }) => (responsive ? "box-sizing: border-box;" : "")};
  width: calc(100% - 30px);
  height: ${({ responsive }) => (responsive ? "40px" : "38px")};
  background: ${(props) => props.theme.colors.white};
  border: 1px solid
    ${(props) =>
      props.error ? props.theme.colors.accent1 : props.theme.colors.grays4};
  border-radius: ${(props) => props.theme.borderRadius.small};
  font-family: ${(props) => props.theme.fonts.normal};
  font-weight: 400;
  font-size: 15px;
  padding-left: 10px;
  outline-color: ${(props) => props.theme.colors.primary1};
  max-width: ${(props) => (props.maxWidth ? "305px" : "")};
  @media (min-width: 600px) {
    width: ${({ responsive }) => (responsive ? "100%" : "295px")};
  }

  &:placeholder-shown {
    background: ${(props) => props.theme.colors.grays9};
    color: ${(props) => props.theme.colors.grays3};
    font-style: italic;
  }
  &:hover {
    background: ${(props) => props.theme.colors.misc3};
  }
  &:focus {
    background: ${(props) => props.theme.colors.white};
    color: ${(props) => props.theme.colors.grays1};
  }
  &:active {
    border: 1px solid ${(props) => props.theme.colors.primary1};
    background: ${(props) => props.theme.colors.white};
    color: ${(props) => props.theme.colors.grays1};
  }
`;

const TextFieldController = (props: TextFieldControllerProps): JSX.Element => {
  const theme = useTheme();
  return (
    <Controller
      name={props.fieldName}
      control={props.control}
      defaultValue={
        props.defaultValue !== null || props.defaultValue !== undefined
          ? props.defaultValue
          : ""
      }
      rules={props.rules}
      render={({
        field: { onChange, onBlur, value, name, ref },
        fieldState: { invalid, isDirty, error, isTouched },
      }) => {
        return (
          <TextFieldWrapper
            className={props.className}
            responsive={props.responsive}
          >
            {props.label ? (
              <StyledLabel responsive={props.responsive}>
                <LabelContainer>
                  <label>{props.label}</label>
                  &nbsp;
                  {props.tooltip ? (
                    <TooltipNew message={props.tooltip} />
                  ) : (
                    <></>
                  )}
                  {!invalid &&
                  isDirty &&
                  isTouched &&
                  !props.noPositiveFeedback ? (
                    <>
                      &nbsp;
                      <FontAwesomeIcon
                        icon={faCheckCircle as IconProp}
                        color={theme.colors.accent7}
                      />
                    </>
                  ) : (
                    <></>
                  )}
                  {(invalid && isDirty) || error ? (
                    <>
                      &nbsp;
                      <FontAwesomeIcon
                        icon={faExclamationCircle as IconProp}
                        color={theme.colors.accent1}
                      />
                    </>
                  ) : (
                    <></>
                  )}
                </LabelContainer>
              </StyledLabel>
            ) : (
              <></>
            )}
            <StyledInput
              error={error !== undefined}
              key={name}
              onBlur={onBlur}
              onChange={onChange}
              ref={ref}
              disabled={props.disabled}
              value={value}
              placeholder={props.placeholder ? props.placeholder : props.label}
              type={props.type ?? "text"}
              responsive={props.responsive}
            />
            {error ? (
              <WarnLabel>
                {props.errorMessage ? props.errorMessage : error?.message}
              </WarnLabel>
            ) : null}
          </TextFieldWrapper>
        );
      }}
    />
  );
};

export default TextFieldController;
